import { verboseDebug } from 'classes/helpers/debug.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { IVerboseComponent } from 'interfaces/i-components';
import { MAX_ON_DEMAND_SHOTS } from 'lib_ts/enums/ellipses';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { ITrainingMsg } from 'lib_ts/interfaces/machine-msg/i-training';
import { uniq } from 'lodash';
import { useEffect } from 'react';
import { useOnDemandStore } from 'stores/use-on-demand-store';

const useOnDemandShotListener = (props: IVerboseComponent) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  useEffect(() => {
    if (!onDemand) {
      return;
    }

    verboseDebug(props, 'listening for training responses');

    const handleNewShot = async (event: CustomEvent) => {
      const data = event.detail as ITrainingMsg;

      verboseDebug(props, 'got training response', data);

      if (!data.success) {
        // ignore interim training messages w/ undefined (e.g. when individual tracking devices report their own detections)
        // ignore failure training messages w/ false
        return;
      }

      const newShot = data.shot;

      if (!newShot) {
        // no shot was created
        return;
      }

      if (!newShot.bs_hash) {
        return;
      }

      if (!newShot.ball_type) {
        return;
      }

      const odms = useOnDemandStore.getState().odmsDict[newShot.bs_hash];

      if (!odms) {
        // shouldn't trigger
        return;
      }

      verboseDebug(props, 'found from store', odms);

      const nextShotIDs = uniq([...odms.shotIDs, newShot._id]).slice(
        -MAX_ON_DEMAND_SHOTS
      );

      const newOdms = await useOnDemandStore.getState().upsert({
        machineID: odms.machineID,
        ball_type: odms.ball_type,
        bs_hash: odms.bs_hash,
        shotIDs: nextShotIDs,
        deleted: false,
      });

      if (!newOdms) {
        console.warn(`${props.identifier}: failed to upsert for new shot`);
        return;
      }

      verboseDebug(props, 'upserted', newOdms);
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, handleNewShot);

    return () => {
      verboseDebug(props, 'stopped listening for training responses');
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, handleNewShot);
    };
  }, [onDemand]);
};

export default useOnDemandShotListener;
